import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AbstractSheetController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetController';
import {AbstractSheetSettingsController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetSettingsController';
import {IMeshContext} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContext';
import {MeshContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContextImpl';
import {CloudSheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cloud/CloudSheet';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {NestedSheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NestedSheet';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {SheetSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettings';
import {
    AnySheet,
    SheetColumnName,
    SheetName
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class WorkbookController
{
    @bind
    public createNewSheet(workbook:Workbook,
                          sheetName:SheetName,
                          slots?:Map<SlotId, Slot>,
                          slotLocationToSlot?:Map<SlotLocationKey, SlotId>,
                          columnNames?:Array<SheetColumnName>,
                          id:SheetId = IdUtil.newGUID()):Sheet
    {
        return Sheet.create(
            id,
            workbook,
            sheetName,
            columnNames,
            slots,
            slotLocationToSlot
        );
    }

    @bind
    public createNewSheetFromFrame(workbook:Workbook,
                                   sheetName:SheetName,
                                   frame:IFrame):NestedSheet
    {
        const sheetId = IdUtil.newGUID();

        const fieldNames:Array<string> = frame.getFieldNames();

        const sheet:NestedSheet = NestedSheet.create(
            sheetId,
            workbook,
            sheetName,
            fieldNames
        );

        sheet.frame = frame;

        return sheet;
    }

    @bind
    public createNewCloudSheet(workbook:Workbook,
                               sheetName:SheetName,
                               columnNames?:Array<SheetColumnName>):CloudSheet
    {
        return CloudSheet.create(
            IdUtil.newGUID(),
            workbook,
            sheetName,
            columnNames
        );
    }

    @bind
    public getNumSheets(workbookId:WorkbookId):number
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheets.length;
    }

    @bind
    public getAllSheets(workbookId:WorkbookId):AnySheet[]
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheets;
    }

    public getNextSheetName(workbookId:WorkbookId):SheetName
    {
        const sheets = this.getAllSheets(workbookId);

        const sheetRe = /^Sheet(\d+)$/;

        const lastSheetNameNumber = Math.max.apply(null,
            sheets.map(sheet =>
            {
                const int = sheetRe.exec(sheet.name);
                return int ? parseInt(int[1]) : 0;
            })
        );

        return `Sheet${lastSheetNameNumber + 1}`;
    }

    @bind
    public getAllCloudSheets(workbookId:WorkbookId):CloudSheet[]
    {
        const allSheets:AnySheet[] = this.getAllSheets(workbookId);

        return allSheets.filter((sheet:AnySheet) => sheet.isCloud) as CloudSheet[];
    }

    @bind
    public isTopLevelSheet(workbookId:WorkbookId, sheetId:SheetId):boolean
    {
        return !!this.findSheetById(workbookId, sheetId);
    }

    @bind
    public findSheetById(workbookId:WorkbookId, sheetId:SheetId):AnySheet | null
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheetsMap.get(sheetId) || null;
    }

    @bind
    public findSheetByName(workbookId:WorkbookId, name:SheetName):AnySheet | null
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheets.find((sheet:AnySheet) => (sheet.name.toUpperCase() === name.toUpperCase()));
    }

    @bind
    public findSheetByIndex(workbookId:WorkbookId, sheetIndex:number):AnySheet | null
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return sheetIndex >= 0 && sheetIndex < workbook.runtime.sheets.length
            ? workbook.runtime.sheets[sheetIndex]
            : null;
    }

    @bind
    public findSheetIndex(workbookId:WorkbookId, sheetId:SheetId):number | -1
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        return workbook.runtime.sheets.findIndex((aSheet:AnySheet) => (aSheet.id === sheetId));
    }

    public getMeshContextBySheetId(sheetId:SheetId):IMeshContext
    {
        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId, true);

        // note: we only have top level sheets in `common` context
        if( sheet.isCloud )
        {
            throw new Error('Cloud sheets are not currently supported');
            // return new MeshContextCloudImpl(sheet.workbook.id, sheetId, sheetId, false);
        }

        return new MeshContextImpl(sheet.workbook.id, sheetId, sheetId, false);
    }

    @bind
    public getSheetCtrl(sheetId:SheetId):AbstractSheetController
    {
        return this.getMeshContextBySheetId(sheetId).sheetCtrl;
    }

    @bind
    public getSheetSettingsCtrl(sheetId:SheetId):AbstractSheetSettingsController
    {
        return this.getMeshContextBySheetId(sheetId).sheetSettingsCtrl;
    }

    @action.bound
    public initializeSheet(sheet:AnySheet):void
    {
        if( !sheet.isCloud )
            commonControllers.spreadsheets.sheet.getOrderedSlots(sheet.id).forEach((slot:Slot) =>
            {
                commonControllers.spreadsheets.sheet.initializeSlot(slot);
                setTimeout(() => commonControllers.spreadsheets.sheet.reEvaluateSlot(slot.sheet.id, slot.location));
            });
    }

    @action.bound
    public async terminateSheet(sheet:AnySheet):Promise<any>
    {
        if( !sheet.isCloud )
            return Promise.all(
                (sheet as Sheet).getSlots().map((slot:Slot) => (
                    commonControllers.spreadsheets.sheet.terminateSlot(slot)
                ))
            );
    }

    @action.bound
    public duplicateAndInsertSheet(workbookId:WorkbookId, existingSheetId:SheetId):SheetId
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);
        const existingSheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(existingSheetId);
        const existingMeshState:MeshState = workbook.view.getMeshStateBySheetId(existingSheetId);
        const newSheetName = `Copy of ${existingSheet.name}`;
        const newSheet:AnySheet = this._createDuplicateSheetOf(existingSheet, newSheetName);
        const sheetIndex = this.findSheetIndex(workbookId, existingSheetId);

        this.insertSheet(workbookId, sheetIndex + 1, newSheet);
        workbook.view.addMeshState(existingMeshState.clone(newSheet));

        const newSheetSettings:SheetSettings = workbook.view.getSheetSettingsById(newSheet.id);

        const existingSettings:SheetSettings = workbook.view.getSheetSettingsById(existingSheetId);
        existingSettings.cellSettings.forEach((cellSettingsId, slotKey) =>
        {
            const cellSettingsObject = workbook.view.cellSettings.get(cellSettingsId);
            const newCellSettingsId = IdUtil.newGUID(); 
            workbook.view.cellSettings.set(newCellSettingsId, cellSettingsObject.clone());
            newSheetSettings.cellSettings.set(slotKey, newCellSettingsId);
        });

        existingSettings.cellNotes.forEach((cellNoteId, slotKey) =>
        {
            const cellNoteObject = workbook.view.cellNotes.get(cellNoteId);
            const newCellNoteId = IdUtil.newGUID(); 
            workbook.view.cellNotes.set(newCellNoteId, cellNoteObject.clone());
            newSheetSettings.cellNotes.set(slotKey, newCellNoteId);
        });

        return newSheet.id;
    }

    @action.bound
    public insertSheet(workbookId:WorkbookId, insertIndex:number, sheet:AnySheet):void
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        if( insertIndex < 0 || insertIndex > workbook.runtime.sheets.length )
        {
            console.error(`insertSheet: Invalid insertIndex: ${insertIndex}`);
            return;
        }

        workbook.runtime.insertSheet(insertIndex, sheet);
        workbook.view.addMeshState(MeshState.create(sheet));
        workbook.view.addSheetSettings(SheetSettings.create(sheet.id, sheet.id, null));

        this.initializeSheet(sheet);

        /**
         * This is a temporary fix. Undo/Redo doesn't work on cloud sheets. It causes removing cloud sheet
         * when we do undo operation after some editing on cloud sheet (since cloud sheet creation is the last item in stack)
         * So, we clear undoRedoStack after cloud sheet is inserted
         */
        if( sheet.isCloud )
        {
            Assert.fail('Re-implement, if it ever comes to this...');
            // const {designState: {undoRedoStack}} = commonControllers.designState.getOpenDesign();
            // undoRedoStack.reset();
        }
    }

    @action.bound
    public removeSheet(workbookId:WorkbookId, sheetId:SheetId):void
    {
        const {main, namedRanges} = commonControllers.spreadsheets;

        const workbook:Workbook = main.findWorkbookById(workbookId);
        const sheet:AnySheet = this.findSheetById(workbookId, sheetId);

        this.terminateSheet(sheet);
        
        this._deleteCellSettings(workbookId, sheetId);
        this._deleteCellNotes(workbookId, sheetId);

        workbook.runtime.deleteSheet(sheetId);
        workbook.view.deleteMeshState(sheetId);
        namedRanges.removeNamedRangeBySheetId(sheetId);

        if( sheet.isCloud )
        {
            throw new Error('Cloud sheets are not currently supported');
            // commonControllers.spreadsheets.cloudSheet.clearAllSlots(sheetId, true);
        }
        else
            workbook.view.deleteSheetSettings(sheetId);
    }

    private _deleteCellSettings(workbookId:WorkbookId, sheetId:SheetId):void
    {
        const {main} = commonControllers.spreadsheets;

        const workbook:Workbook = main.findWorkbookById(workbookId);

        const sheetSettings = workbook.view.getSheetSettingsById(sheetId);

        sheetSettings.cellSettings.forEach((cellSettingsId) => workbook.view.deleteCellSettings(cellSettingsId));
    }

    private _deleteCellNotes(workbookId:WorkbookId, sheetId:SheetId):void
    {
        const {main} = commonControllers.spreadsheets;

        const workbook:Workbook = main.findWorkbookById(workbookId);

        const sheetSettings = workbook.view.getSheetSettingsById(sheetId);

        sheetSettings.cellNotes.forEach((cellNoteId) => workbook.view.removeCellNote(cellNoteId));
    }

    @action.bound
    public moveSheet(workbookId:WorkbookId, sheetId:SheetId, index:number):void
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        workbook.runtime.moveSheet(index, sheetId);
    }

    @action.bound
    public renameSheet(workbookId:WorkbookId, sheetId:SheetId, name:string):void
    {
        const workbook:Workbook = commonControllers.spreadsheets.main.findWorkbookById(workbookId);

        workbook.runtime.renameSheet(sheetId, name);
    }

    public getUniqueSheetName(workbookId:WorkbookId, sheetName:SheetName):SheetName
    {
        const {main} = commonControllers.spreadsheets;
        const workbook:Workbook = main.findWorkbookById(workbookId);

        const existingNames = workbook.runtime.sheets.map(({name}) => name);

        let newName = sheetName;
        let index = 1;

        while( true )
        {
            if( existingNames.indexOf(newName) === -1 )
                break;

            newName = `${sheetName} (${index})`;
            index += 1;
        }

        return newName;
    }

    @bind
    private _createDuplicateSheetOf(existingSheet:AnySheet, newSheetName:SheetName):AnySheet
    {
        const clonedSheet:AnySheet = existingSheet.clone();

        clonedSheet.name = newSheetName;

        return clonedSheet;
    }

    // @bind
    // private async _buildSlotsFromFrameValues(frame:IFrame, sheet:AnySheet):Promise<Map<SlotId, NestedFrameSlot>>
    // {
    //     const records:Array<IFrameRecord> = frame.getRecords();
    //     const fieldNames:number = frame.fieldCount;
    //
    //     let x:ColumnIndex = 0;
    //     let y:RowIndex = 0;
    //
    //     const items:{record:IFrameRecord, x:number, y:number}[] = [];
    //
    //     for( let record of records )
    //     {
    //         for( let i = 0; i < fieldNames; i++ )
    //         {
    //             items.push({record, x, y});
    //             x++;
    //         }
    //
    //         x = 0;
    //         y++;
    //     }
    //
    //     const slots:Map<SlotId, NestedFrameSlot> = new Map();
    //
    //     await ArrayUtils.asyncForEach(items, ({record, x, y}) =>
    //     {
    //         const cell:IValue = record.getCellAtOffset(x);
    //         const slot:NestedFrameSlot = NestedFrameSlot.create(
    //             sheet as NestedSheet,
    //             cell,
    //             new SlotLocation(x, y)
    //         );
    //
    //         slots.set(slot.id, slot);
    //     }, 5);
    //
    //     return slots;
    // }

    // @bind
    // private async _buildSlotLocationsFromSots(slots:Map<SlotId, Slot | NestedFrameSlot>):Promise<Map<SlotLocationKey, SlotId>>
    // {
    //     const slotLocationToSlot:Map<SlotLocationKey, SlotId> = new Map();
    //
    //     await ArrayUtils.asyncForEach(Array.from(slots.values()), (slot:Slot) => (
    //         slotLocationToSlot.set(SlotLocation.toKey(slot.location), slot.id)
    //     ), 5);
    //
    //     return slotLocationToSlot;
    // }
}
